import { ActivityEntityName, ActivityShareType, DomainType, EntityViewDisplay, MetadataAttachment } from '@jms/model';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { Command } from '@workbuddy/ui/model';

/**
 * File collection board — parent-scoped attachments (job / stage / contact / …).
 *
 * Same entity-board shell as every other entity (header filters, display, bulk, cmdk).
 * Body is first-class {@link EntityViewDisplay.Attachments} (`board-attachments`), not a
 * custom component and not Table/Card/List.
 *
 * Data: `TenantFileEntityDataStrategy` — always needs `header.parentId` (or legacy `entityIds`).
 *
 * Display settings: product rows are generic {@link EntityBoardDisplaySettingsExtension}
 * contributions under namespace `file-scope` (sharing / file-only / showAll). The shell paints
 * controls; the strategy reads `preview.extensions['file-scope']`.
 *
 * Group options are the `groupable` File metadata fields (Source, Tags, Task / SOR, …); `board-attachments` groups client-side.
 */
export const fileManifest = registerManifest<MetadataAttachment>({
  entity: ActivityEntityName.File,
  slug: 'file',

  board: {
    supportedDisplays: [EntityViewDisplay.Attachments],

    bulkActionHandlers: {
      // Client zip — prepareBulkAction returns `handled` and skips the bulk API pipeline.
      'files.download': () => import('./bulk.actions/file.download.bulk.action').then(module => module.handleFileDownloadBulkAction),
    },

    defaults: {
      header: {
        title: 'Files',
        showSearch: true,
        showPaginator: true,
        showViewOptions: false,
        hideAdd: true,
      },
      itemInteraction: {
        selectable: {
          type: 'multiple',
          bulkCommands: [
            new Command({
              _id: 'files.download',
              label: 'Download selected',
              iconLeft: 'fa-regular fa-download',
            }),
          ],
        },
      },
      /**
       * File-scope display options (legacy files-dashboard viewOptions).
       * Paths land on `view.extensions['file-scope']` → items request `preview.extensions`.
       */
      displaySettings: {
        contributions: [
          {
            id: 'file-scope',
            when: ({ display }) => display === EntityViewDisplay.Attachments,
            controls: [
              {
                kind: 'dropdown',
                id: 'sharing',
                label: 'Shared With',
                path: 'extensions.file-scope.sharing',
                options: [
                  { id: 'All', label: 'All' },
                  { id: ActivityShareType.Customer, label: 'Customer' },
                  { id: ActivityShareType.Field, label: 'Field' },
                  { id: ActivityShareType.NotShared, label: 'Not Shared' },
                ],
                default: 'All',
              },
              // Filter loaded rows: only real file blobs (`isFile`). Off = also notes/links/etc in the feed.
              {
                kind: 'switch',
                id: 'fileActivitiesOnly',
                label: 'Show files only',
                path: 'extensions.file-scope.fileActivitiesOnly',
                default: false,
              },
              // No "Show all" control: legacy only mattered on WorkOrder (Job was always on + disabled).
              // TenantFileEntityDataStrategy already defaults showAll by parent (Job=true, else false).
            ],
          },
        ],
      },
    },

    // isEmbedded = parent-scoped query/views (parentId). Not the same as header.compact
    // (narrow chrome / filter overlay). Compact is mount- or density-driven only.
    embedded: {
      header: {
        title: 'Files',
        showSearch: true,
        showPaginator: false,
        showViewOptions: false,
        hideAdd: true,
      },
    },
  },

  /**
   * Client Portal Files board — staged, not yet routed (see apps/portal application.module).
   * A domain block replaces the base manifest rather than merging, so everything is restated here.
   * No displaySettings: the API already drops rows that are not Customer-shared.
   */
  domains: {
    [DomainType.ClientPortal]: {
      board: {
        supportedDisplays: [EntityViewDisplay.Attachments],

        bulkActionHandlers: {
          // Client zip — prepareBulkAction returns `handled` and skips the bulk API pipeline.
          'files.download': () => import('./bulk.actions/file.download.bulk.action').then(module => module.handleFileDownloadBulkAction),
        },

        defaults: {
          readonly: true,
          header: {
            title: 'Files',
            showSearch: true,
            showPaginator: true,
            showViewOptions: false,
            hideAdd: true,
          },
          // Download only — never mutate.
          itemInteraction: {
            selectable: {
              type: 'multiple',
              bulkCommands: [
                new Command({
                  _id: 'files.download',
                  label: 'Download selected',
                  iconLeft: 'fa-regular fa-download',
                }),
              ],
            },
          },
        },
      },
    },
  },
});
